import { AnimationEvent, CSSProperties, ReactNode } from 'react';

import { ColorMode, useColorMode, useMediaQuery } from '@chakra-ui/react';
import { BorderBeam, type BorderBeamTheme } from 'border-beam';

export type WorkBeamPreset = 'line' | 'pulse';

export type WorkBeamProps = {
  active: boolean;
  preset: WorkBeamPreset;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  borderRadius?: number;
};

const LINE_STRENGTH = 0.5;
const PULSE_STRENGTH = 0.4;

function beamThemeFromColorMode(colorMode: ColorMode): BorderBeamTheme {
  switch (colorMode) {
    case 'light':
      return 'light';
    case 'dark':
      return 'dark';
    default: {
      const _exhaustive: never = colorMode;
      return _exhaustive;
    }
  }
}

function beamSizeFromPreset(preset: WorkBeamPreset) {
  switch (preset) {
    case 'line':
      return 'line' as const;
    case 'pulse':
      return 'pulse-inner' as const;
    default: {
      const _exhaustive: never = preset;
      return _exhaustive;
    }
  }
}

function strengthFromPreset(preset: WorkBeamPreset) {
  switch (preset) {
    case 'line':
      return LINE_STRENGTH;
    case 'pulse':
      return PULSE_STRENGTH;
    default: {
      const _exhaustive: never = preset;
      return _exhaustive;
    }
  }
}

const beamChildStyle = {
  width: '100%',
  minWidth: 0,
  minHeight: 0,
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
} satisfies CSSProperties;

/**
 * BorderBeam's root `onAnimationEnd` does `animationName.includes(...)`.
 * Descendant Chakra/menu events can omit the name and crash the page.
 */
function stopDescendantAnimationEnd(event: AnimationEvent<HTMLDivElement>) {
  event.stopPropagation();
}

export function WorkBeam({
  active,
  preset,
  children,
  className,
  style,
  borderRadius,
}: WorkBeamProps) {
  const { colorMode } = useColorMode();
  const [prefersReducedMotion] = useMediaQuery(
    '(prefers-reduced-motion: reduce)'
  );
  const wrapperStyle = {
    width: '100%',
    minWidth: 0,
    ...(borderRadius == null ? {} : { borderRadius }),
    ...style,
  } satisfies CSSProperties;

  // Keep one wrapper type so toggling `active` does not remount children.
  return (
    <BorderBeam
      size={beamSizeFromPreset(preset)}
      colorVariant='ocean'
      theme={beamThemeFromColorMode(colorMode)}
      strength={strengthFromPreset(preset)}
      active={active && !prefersReducedMotion}
      borderRadius={borderRadius}
      className={className}
      style={wrapperStyle}
      data-cy='work-beam'
      data-preset={preset}
    >
      <div
        style={beamChildStyle}
        onAnimationEnd={stopDescendantAnimationEnd}
      >
        {children}
      </div>
    </BorderBeam>
  );
}
